import { Component, Input, OnDestroy, OnInit } from '@angular/core';
import { AbstractControl, FormControl, FormGroup, ValidationErrors, Validators } from '@angular/forms';
import { barcodeValidator, extractApiErrorMessage, isApiSuccess } from 'src/app/services/barcode.util';
import { NbDialogRef } from '@nebular/theme';
import { NgxSpinnerService } from 'ngx-spinner';
import { ToastrService } from 'ngx-toastr';
import { EcommerceService } from 'src/app/services/ecommerce.service';
import { GlobalProductService } from 'src/app/services/global-product.service';
import { GlobalCategoryService } from 'src/app/services/global-category.service';
import { ImageUrlService } from 'src/app/services/image-url.service';
import {
  appendIngredientsNutritionFormData,
  EU_ALLERGEN_OPTIONS,
  INGREDIENTS_MAX_LENGTH,
  INGREDIENTS_SOFT_WARN_LENGTH,
  isAllergenSelected,
  NUTRITION_FIELDS,
  patchIngredientsNutrition,
  createNutritionFormGroup,
  toggleAllergenSelection
} from 'src/app/services/product-nutrition.util';
import { isLiquidUnitCode, UNIT_CODE_OPTIONS } from 'src/app/services/unit-pricing.util';
import {
  appendVendorOrderFormData,
  coliValidator,
  patchVendorOrder,
  shelfLifeDaysValidator
} from 'src/app/services/product-vendor-order.util';
import { GlobalReferenceService, DskGroupSearchItem, VendorSearchItem } from 'src/app/services/global-reference.service';
import {
  formatDskSearchOptionLabel,
  formatDskSearchOptionMeta,
  patchDskClassification,
  patchVendorFields
} from 'src/app/services/global-reference.util';
import { ProductFeatureFlagsService } from 'src/app/services/product-feature-flags.service';
import { Subject, of } from 'rxjs';
import { catchError, debounceTime, distinctUntilChanged, finalize, switchMap, takeUntil } from 'rxjs/operators';

@Component({
  selector: 'app-global-product-form-dialog',
  templateUrl: './global-product-form-dialog.component.html',
  styleUrls: ['./global-product-form-dialog.component.scss']
})
export class GlobalProductFormDialogComponent implements OnInit, OnDestroy {

  @Input() product: any = null;
  @Input() chainId: string | null = null;

  form!: FormGroup;
  saving = false;

  depositFeeTypes: any[] = [];
  productImages: File[] = [];
  productImagePreviews: string[] = [];
  existingImages: any[] = [];
  primaryImageIndex = 0;
  readonly unitCodeOptions = UNIT_CODE_OPTIONS;
  readonly allergenOptions = EU_ALLERGEN_OPTIONS;
  readonly nutritionFields = NUTRITION_FIELDS;
  readonly ingredientsMaxLength = INGREDIENTS_MAX_LENGTH;
  readonly ingredientsSoftWarnLength = INGREDIENTS_SOFT_WARN_LENGTH;

  showAllergens = false;
  showNutrition = false;

  dskSearchInput$ = new Subject<string>();
  vendorSearchInput$ = new Subject<string>();
  dskSearchResults: DskGroupSearchItem[] = [];
  vendorSearchResults: VendorSearchItem[] = [];
  dskSearchLoading = false;
  vendorSearchLoading = false;
  selectedDskSearch: DskGroupSearchItem | null = null;
  selectedVendorSearch: VendorSearchItem | null = null;
  formatDskSearchOptionLabel = formatDskSearchOptionLabel;
  formatDskSearchOptionMeta = formatDskSearchOptionMeta;
  private destroy$ = new Subject<void>();

  catalogSearchTerm = '';
  catalogSearchResults: any[] = [];
  catalogSearchLoading = false;
  catalogSearchDone = false;
  catalogTemplateLoading = false;
  private catalogSearchInput$ = new Subject<string>();

  globalCategories: any[] = [];
  globalCategoriesLoading = false;

  constructor(
    private dialogRef: NbDialogRef<GlobalProductFormDialogComponent>,
    private globalProductService: GlobalProductService,
    private globalCategoryService: GlobalCategoryService,
    private globalReferenceService: GlobalReferenceService,
    private ecommerceService: EcommerceService,
    private imageUrlService: ImageUrlService,
    private spinner: NgxSpinnerService,
    private toastr: ToastrService,
    private productFeatureFlagsService: ProductFeatureFlagsService
  ) {}

  ngOnInit(): void {
    this.buildForm();
    this.productFeatureFlagsService.getFlags().pipe(takeUntil(this.destroy$)).subscribe((flags) => {
      this.showAllergens = flags.productAllergensEnabled;
      this.showNutrition = flags.productNutritionEnabled;
      this.applyProductFeatureFlagControls();
    });
    this.ecommerceService.getDepositFeeTypes().subscribe({
      next: (res: any) => {
        this.depositFeeTypes = res?.response?.data || res?.data || [];
        // Apply validators now that we know whether fee types exist
        if (this.form.get('hasDeposit')?.value) {
          this.applyDepositValidators(true);
        }
      }
    });
    if (this.product) {
      this.populateForm(this.product);
    }
    this.setupReferenceTypeaheads();
    this.setupCatalogTemplateSearch();
    this.loadGlobalCategories();
  }

  loadGlobalCategories(): void {
    this.globalCategoriesLoading = true;
    this.globalCategoryService.listCategories().subscribe({
      next: (res: any) => {
        this.globalCategories = this.globalCategoryService.extractListData(res).categories;
        this.globalCategoriesLoading = false;
      },
      error: () => {
        this.globalCategories = [];
        this.globalCategoriesLoading = false;
      }
    });
  }

  globalCategoryOptionLabel(cat: any): string {
    const depth = cat.depth ?? 0;
    const label = '\u00a0\u00a0'.repeat(depth) + (cat.path || cat.name);
    return cat.status === 0 ? `${label} (inactive)` : label;
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  private setupReferenceTypeaheads(): void {
    this.dskSearchInput$.pipe(
      debounceTime(250),
      distinctUntilChanged(),
      switchMap((term) => {
        if (!term || term.length < 1) {
          return of({ items: [] as DskGroupSearchItem[] });
        }
        this.dskSearchLoading = true;
        return this.globalReferenceService.searchDskGroups({ q: term, limit: 50 }).pipe(
          catchError(() => of({ items: [] as DskGroupSearchItem[], total: 0 })),
          finalize(() => { this.dskSearchLoading = false; })
        );
      }),
      takeUntil(this.destroy$)
    ).subscribe((res) => {
      this.dskSearchResults = res.items || [];
    });

    this.vendorSearchInput$.pipe(
      debounceTime(250),
      distinctUntilChanged(),
      switchMap((term) => {
        if (!term || term.length < 1) {
          return of({ items: [] as VendorSearchItem[] });
        }
        this.vendorSearchLoading = true;
        return this.globalReferenceService.searchVendors(term, 50).pipe(
          catchError(() => of({ items: [] as VendorSearchItem[], total: 0 })),
          finalize(() => { this.vendorSearchLoading = false; })
        );
      }),
      takeUntil(this.destroy$)
    ).subscribe((res) => {
      this.vendorSearchResults = res.items || [];
    });
  }

  private setupCatalogTemplateSearch(): void {
    this.catalogSearchInput$.pipe(
      debounceTime(350),
      distinctUntilChanged(),
      switchMap((term) => {
        if (term.trim().length < 2) {
          this.catalogSearchDone = false;
          this.catalogSearchResults = [];
          return of(null);
        }
        this.catalogSearchLoading = true;
        this.catalogSearchDone = false;
        const request$ = this.chainId
          ? this.globalProductService.listChainProducts(this.chainId, { search: term.trim() })
          : this.globalProductService.listPlatformProducts({ search: term.trim() });
        return request$.pipe(
          catchError(() => of(null)),
          finalize(() => { this.catalogSearchLoading = false; })
        );
      }),
      takeUntil(this.destroy$)
    ).subscribe((res: any) => {
      if (res === null) {
        if (this.catalogSearchTerm.trim().length >= 2) {
          this.catalogSearchResults = [];
          this.catalogSearchDone = true;
        }
        return;
      }
      const products = res?.response?.data?.products || res?.data?.products || [];
      const selfId = this.product?._id != null ? String(this.product._id) : null;
      this.catalogSearchResults = products.filter(
        (p: any) => !selfId || String(p._id) !== selfId
      );
      this.catalogSearchDone = true;
    });
  }

  onCatalogSearchInput(term: string): void {
    this.catalogSearchInput$.next(term);
  }

  selectCatalogTemplate(gp: any): void {
    this.catalogSearchTerm = '';
    this.catalogSearchResults = [];
    this.catalogSearchDone = false;
    this.catalogSearchInput$.next('');
    this.catalogTemplateLoading = true;
    this.globalProductService.getGlobalProduct(gp._id).pipe(
      finalize(() => { this.catalogTemplateLoading = false; })
    ).subscribe({
      next: (res: any) => {
        const full = res?.response?.data || res?.data || null;
        const doc = full || gp;
        this.populateForm(doc, { includeImages: false });
        this.toastr.success('Fields filled from catalog template — review and save.');
      },
      error: (err: any) => {
        this.toastr.error(extractApiErrorMessage(err, 'Could not load catalog product'));
      }
    });
  }

  onDskSearchSelected(item: DskGroupSearchItem | null): void {
    if (!item) {
      return;
    }
    patchDskClassification(this.form, item);
    this.selectedDskSearch = null;
  }

  onVendorSearchSelected(item: VendorSearchItem | null): void {
    if (!item) {
      return;
    }
    patchVendorFields(this.form, item);
    this.selectedVendorSearch = null;
  }

  buildForm(): void {
    this.form = new FormGroup({
      name: new FormControl('', [Validators.required]),
      description: new FormControl(''),
      barcode: new FormControl('', [barcodeValidator]),
      sku: new FormControl(''),
      weight: new FormControl(null),
      length: new FormControl(null),
      width: new FormControl(null),
      height: new FormControl(null),
      ageRestricted: new FormControl(false),
      minimumAge: new FormControl(null),
      hasDeposit: new FormControl(false),
      depositCategory: new FormControl(''),
      depositFeeTypeId: new FormControl(''),
      providerDepositCode: new FormControl(''),
      tagsInput: new FormControl(''),
      metaTitle: new FormControl(''),
      metaDescription: new FormControl(''),
      mainGroup: new FormControl(''),
      productGroup: new FormControl(''),
      subGroup: new FormControl(''),
      dskAssortmentID: new FormControl(''),
      family: new FormControl(''),
      discountGroup: new FormControl(''),
      unitCode: new FormControl(''),
      unitContent: new FormControl(null, [GlobalProductFormDialogComponent.unitContentValidator]),
      ingredients: new FormControl(''),
      allergens: new FormControl<string[]>([]),
      allergenTraces: new FormControl<string[]>([]),
      nutrition: createNutritionFormGroup(),
      vendorID: new FormControl(''),
      vendorName: new FormControl(''),
      orderNumber: new FormControl(''),
      producer: new FormControl(''),
      country: new FormControl(''),
      coli: new FormControl(null, [coliValidator]),
      shelfLifeDays: new FormControl(null, [shelfLifeDaysValidator]),
      defaultCostPrice: new FormControl(null, [Validators.min(0)]),
      defaultSellPrice: new FormControl(null, [Validators.min(0)]),
      globalCategoryId: new FormControl('')
    });

    this.form.get('unitCode')?.valueChanges.subscribe(() => {
      this.form.get('unitContent')?.updateValueAndValidity({ emitEvent: false });
    });

    this.form.get('ageRestricted')?.valueChanges.subscribe(ageRestricted => {
      const minimumAgeControl = this.form.get('minimumAge');
      if (ageRestricted) {
        minimumAgeControl?.setValidators([Validators.required, Validators.min(1)]);
      } else {
        minimumAgeControl?.clearValidators();
        minimumAgeControl?.setValue(null);
      }
      minimumAgeControl?.updateValueAndValidity();
    });

    this.form.get('hasDeposit')?.valueChanges.subscribe(hasDeposit => {
      this.applyDepositValidators(hasDeposit);
    });
  }

  static unitContentValidator(control: AbstractControl): ValidationErrors | null {
    const unitCode = control.parent?.get('unitCode')?.value;
    const unitContent = control.value;
    if (isLiquidUnitCode(unitCode) && unitContent === 0) {
      return {
        unitContentZero: {
          message: 'Unit content must be greater than 0 for liquid unit codes (CL, ML, L)'
        }
      };
    }
    return null;
  }

  private applyDepositValidators(hasDeposit: boolean): void {
    const depositCategoryControl = this.form.get('depositCategory');
    const depositFeeTypeIdControl = this.form.get('depositFeeTypeId');
    if (hasDeposit) {
      if (this.depositFeeTypes.length > 0) {
        depositFeeTypeIdControl?.setValidators([Validators.required]);
        depositCategoryControl?.clearValidators();
      } else {
        depositCategoryControl?.setValidators([Validators.required]);
        depositFeeTypeIdControl?.clearValidators();
      }
    } else {
      depositCategoryControl?.clearValidators();
      depositCategoryControl?.setValue('');
      depositFeeTypeIdControl?.clearValidators();
      depositFeeTypeIdControl?.setValue('');
    }
    depositCategoryControl?.updateValueAndValidity();
    depositFeeTypeIdControl?.updateValueAndValidity();
  }

  populateForm(p: any, opts: { includeImages?: boolean } = {}): void {
    this.form.patchValue({
      name: p.name || '',
      description: p.description || '',
      barcode: p.barcode || '',
      sku: p.sku || '',
      weight: p.weight || null,
      length: p.dimensions?.length || null,
      width: p.dimensions?.width || null,
      height: p.dimensions?.height || null,
      ageRestricted: p.ageRestricted || false,
      minimumAge: p.minimumAge || null,
      hasDeposit: p.hasDeposit || false,
      depositCategory: p.depositCategory || '',
      depositFeeTypeId: (typeof p.depositFeeTypeId === 'object' ? p.depositFeeTypeId?._id : p.depositFeeTypeId) || '',
      providerDepositCode: p.providerDepositCode || '',
      metaTitle: p.metaTitle || '',
      metaDescription: p.metaDescription || '',
      mainGroup: p.mainGroup || '',
      productGroup: p.productGroup || '',
      subGroup: p.subGroup || '',
      dskAssortmentID: p.dskAssortmentID || '',
      family: p.family || '',
      discountGroup: p.discountGroup || '',
      unitCode: p.unitCode || '',
      unitContent: p.unitContent ?? null,
      defaultCostPrice: p.defaultCostPrice ?? null,
      defaultSellPrice: p.defaultSellPrice ?? null,
      globalCategoryId: p.globalCategoryId || p.globalCategory?._id || ''
    });

    if (p.tags?.length) {
      this.form.get('tagsInput')?.setValue(p.tags.join(', '));
    } else {
      this.form.get('tagsInput')?.setValue('');
    }

    patchIngredientsNutrition(this.form, p);
    patchVendorOrder(this.form, p);
    this.applyProductFeatureFlagControls();

    if (opts.includeImages !== false) {
      this.populateImages(p);
    }
  }

  private populateImages(p: any): void {
    const isValidImageUrl = (url: string): boolean => {
      if (!url) return false;
      const lower = url.toLowerCase();
      if (lower === 'null' || lower === 'undefined' || lower === '') return false;
      if (lower.endsWith('/null') || lower.endsWith('/undefined')) return false;
      return true;
    };

    // Build existing images list, filtering out null/invalid URLs from CSV-imported products
    this.existingImages = (p.images || [])
      .map((img: any) => ({
        url: this.imageUrlService.fixImageUrl(typeof img === 'string' ? img : img?.url || ''),
        alt: img?.alt || '',
        isPrimary: img?.isPrimary || false
      }))
      .filter((img: any) => isValidImageUrl(img.url));
    this.productImagePreviews = this.existingImages.map(img => img.url);
    this.primaryImageIndex = Math.max(0, this.existingImages.findIndex(img => img.isPrimary));
    this.productImages = [];
  }

  get ageRestricted(): boolean {
    return this.form.get('ageRestricted')?.value === true;
  }

  get hasDeposit(): boolean {
    return this.form.get('hasDeposit')?.value === true;
  }

  get ingredientsCharCount(): number {
    return (this.form.get('ingredients')?.value || '').length;
  }

  private applyProductFeatureFlagControls(): void {
    if (!this.form) {
      return;
    }
    for (const name of ['ingredients', 'allergens', 'allergenTraces'] as const) {
      const control = this.form.get(name);
      if (this.showAllergens) {
        control?.enable({ emitEvent: false });
      } else {
        control?.disable({ emitEvent: false });
      }
    }
    const nutrition = this.form.get('nutrition');
    if (this.showNutrition) {
      nutrition?.enable({ emitEvent: false });
    } else {
      nutrition?.disable({ emitEvent: false });
    }
  }

  isAllergenSelected(field: 'allergens' | 'allergenTraces', value: string): boolean {
    return isAllergenSelected(this.form.get(field)?.value, value);
  }

  toggleAllergen(field: 'allergens' | 'allergenTraces', value: string, checked: boolean): void {
    const control = this.form.get(field);
    const updated = toggleAllergenSelection(control?.value || [], value, checked);
    control?.setValue(updated);
    control?.markAsTouched();
  }

  onImagesSelect(event: any): void {
    const files = Array.from(event.target.files) as File[];
    files.forEach(file => {
      if (file.type.startsWith('image/')) {
        this.productImages.push(file);
        const reader = new FileReader();
        reader.onload = (e: any) => this.productImagePreviews.push(e.target.result);
        reader.readAsDataURL(file);
      }
    });
  }

  removeImage(index: number): void {
    if (index < this.existingImages.length) {
      this.existingImages.splice(index, 1);
    } else {
      this.productImages.splice(index - this.existingImages.length, 1);
    }
    this.productImagePreviews.splice(index, 1);
    if (this.primaryImageIndex >= this.productImagePreviews.length) {
      this.primaryImageIndex = 0;
    }
  }

  setPrimaryImage(index: number): void {
    this.primaryImageIndex = index;
  }

  parseTagsInput(): string[] {
    const raw = this.form.get('tagsInput')?.value || '';
    return raw.split(',').map((t: string) => t.trim()).filter((t: string) => t.length > 0);
  }

  buildFormData(): FormData {
    const fd = new FormData();
    const v = this.form.value;

    fd.append('name', v.name || '');
    fd.append('description', v.description || '');
    fd.append('barcode', v.barcode || '');
    fd.append('sku', v.sku || '');
    fd.append('scope', this.chainId ? 'chain' : 'platform');
    if (this.chainId) fd.append('chainId', this.chainId);
    if (v.weight !== null && v.weight !== undefined && v.weight !== '') {
      fd.append('weight', String(v.weight));
    }
    if (v.length !== null && v.length !== undefined && v.length !== '') {
      fd.append('dimensions[length]', String(v.length));
    }
    if (v.width !== null && v.width !== undefined && v.width !== '') {
      fd.append('dimensions[width]', String(v.width));
    }
    if (v.height !== null && v.height !== undefined && v.height !== '') {
      fd.append('dimensions[height]', String(v.height));
    }
    fd.append('ageRestricted', String(v.ageRestricted || false));
    fd.append('minimumAge', String(v.minimumAge || 0));
    fd.append('hasDeposit', String(v.hasDeposit || false));
    if (v.hasDeposit) {
      if (v.depositFeeTypeId) fd.append('depositFeeTypeId', v.depositFeeTypeId);
      if (v.depositCategory) fd.append('depositCategory', v.depositCategory);
      if (v.providerDepositCode) fd.append('providerDepositCode', v.providerDepositCode);
    }
    fd.append('tags', JSON.stringify(this.parseTagsInput()));
    fd.append('metaTitle', v.metaTitle || '');
    fd.append('metaDescription', v.metaDescription || '');
    fd.append('mainGroup', v.mainGroup || '');
    fd.append('productGroup', v.productGroup || '');
    fd.append('subGroup', v.subGroup || '');
    fd.append('dskAssortmentID', v.dskAssortmentID || '');
    fd.append('family', v.family || '');
    fd.append('discountGroup', v.discountGroup || '');
    fd.append('unitCode', v.unitCode || '');
    if (v.unitContent !== null && v.unitContent !== undefined && v.unitContent !== '') {
      fd.append('unitContent', String(v.unitContent));
    } else {
      fd.append('unitContent', '');
    }

    appendIngredientsNutritionFormData(fd, v, {
      includeAllergens: this.showAllergens,
      includeNutrition: this.showNutrition,
    });
    appendVendorOrderFormData(fd, v);

    const defaultCost = v.defaultCostPrice;
    const defaultSell = v.defaultSellPrice;
    fd.append('defaultCostPrice', defaultCost === null || defaultCost === undefined || defaultCost === '' ? '' : String(defaultCost));
    fd.append('defaultSellPrice', defaultSell === null || defaultSell === undefined || defaultSell === '' ? '' : String(defaultSell));
    fd.append('globalCategoryId', v.globalCategoryId || '');

    fd.append('existingImages', JSON.stringify(this.existingImages));

    this.productImages.forEach((file, i) => {
      fd.append('images', file);
      fd.append(`imageAlt_${i}`, file.name);
      fd.append(`isPrimary_${i}`, String(i + this.existingImages.length === this.primaryImageIndex));
    });

    return fd;
  }

  save(): void {
    if (this.form.invalid) {
      this.form.markAllAsTouched();
      const barcodeMessage = this.form.get('barcode')?.errors?.['invalidBarcode']?.message;
      this.toastr.error(barcodeMessage || 'Please fill in all required fields');
      return;
    }

    const formData = this.buildFormData();
    this.saving = true;
    this.spinner.show();

    let request$;
    if (this.product) {
      request$ = this.globalProductService.updateGlobalProduct(this.product._id, formData);
    } else if (this.chainId) {
      request$ = this.globalProductService.createChainGlobalProduct(this.chainId, formData);
    } else {
      request$ = this.globalProductService.createGlobalProduct(formData);
    }

    request$.subscribe({
      next: (res: any) => {
        this.spinner.hide();
        this.saving = false;
        if (!isApiSuccess(res)) {
          this.toastr.error(res?.response?.message || 'Save failed');
          return;
        }
        this.toastr.success(this.product ? 'Product updated' : 'Product created');
        this.dialogRef.close(true);
      },
      error: (err: any) => {
        this.spinner.hide();
        this.saving = false;
        this.toastr.error(extractApiErrorMessage(err, 'Save failed'));
      }
    });
  }

  onBarcodeInput(): void {
    const barcodeControl = this.form.get('barcode');
    barcodeControl?.markAsTouched();
    barcodeControl?.updateValueAndValidity({ emitEvent: false });
  }

  onImageError(event: any): void {
    event.target.style.display = 'none';
  }

  cancel(): void {
    this.dialogRef.close(false);
  }
}
